import type { UserContextEvent } from '../telemetry-models';

/**
 * Network information including connection type, speed, and data saver status
 */
export type NetworkInfo = Pick<UserContextEvent, 'effective_type' | 'downlink_mbps' | 'rtt_ms' | 'save_data_enabled'>;

/**
 * Thresholds for detecting significant network changes
 */
export interface NetworkChangeThresholds {
  rtt: number; // RTT threshold in milliseconds
  downlink: number; // Downlink threshold in Mbps
}

/**
 * Type for Network Information API
 */
export interface NetworkInformation extends EventTarget {
  effectiveType: '2g' | '3g' | '4g' | 'slow-2g';
  downlink: number;
  rtt: number;
  saveData: boolean;
}

/**
 * Detects network connection information
 * Implements the IDetector<NetworkInfo> pattern using static methods
 */
export class NetworkDetector {
  /**
   * Capture current network information
   */
  static capture(): NetworkInfo {
    if (!('connection' in navigator)) {
      // Fallback: estimate from Navigation Timing
      const estimated = this.estimateFromNavigationTiming();
      return {
        effective_type: estimated.effectiveType,
        downlink_mbps: undefined,
        rtt_ms: estimated.rttMs,
        save_data_enabled: false,
      };
    }

    const conn = (navigator as Navigator & { connection?: NetworkInformation }).connection;
    if (!conn) {
      return {
        effective_type: undefined,
        downlink_mbps: undefined,
        rtt_ms: undefined,
        save_data_enabled: false,
      };
    }

    const downlinkMbps = conn.downlink;
    const rttMs = conn.rtt;

    return {
      effective_type: conn.effectiveType,
      downlink_mbps: downlinkMbps,
      rtt_ms: rttMs,
      save_data_enabled: conn.saveData || false,
    };
  }

  /**
   * Estimate network type from Navigation Timing API
   */
  static estimateFromNavigationTiming(): {
    effectiveType: 'slow-2g' | '2g' | '3g' | '4g';
    rttMs?: number;
  } {
    try {
      const navTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navTiming && navTiming.responseStart && navTiming.requestStart) {
        const rttMs = navTiming.responseStart - navTiming.requestStart;

        let effectiveType: 'slow-2g' | '2g' | '3g' | '4g';
        if (rttMs < 50) effectiveType = '4g';
        else if (rttMs < 150) effectiveType = '3g';
        else if (rttMs < 400) effectiveType = '2g';
        else effectiveType = 'slow-2g';

        return { effectiveType, rttMs };
      }
    } catch (e) {
      // Navigation Timing not available
    }

    // Ultimate fallback
    return { effectiveType: '4g' };
  }

  /**
   * Check if network change is significant based on thresholds
   */
  static isSignificantChange(old: NetworkInfo, current: NetworkInfo, thresholds: NetworkChangeThresholds): boolean {
    // Effective type changed
    if (old.effective_type !== current.effective_type) return true;

    // RTT changed significantly
    if (
      old.rtt_ms !== undefined &&
      current.rtt_ms !== undefined &&
      Math.abs(old.rtt_ms - current.rtt_ms) > thresholds.rtt
    ) {
      return true;
    }

    // Downlink changed significantly
    return (
      old.downlink_mbps !== undefined &&
      current.downlink_mbps !== undefined &&
      Math.abs(old.downlink_mbps - current.downlink_mbps) > thresholds.downlink
    );
  }

  /**
   * Get the Network Information API connection object if available
   */
  static getConnection(): NetworkInformation | null {
    if ('connection' in navigator) {
      return (navigator as Navigator & { connection?: NetworkInformation }).connection || null;
    }
    return null;
  }
}
